import { useMemo, useCallback } from 'react';
import { WalletStore, ZenJsUtils, SettingsStore } from '@zen/common-app-parts';
import { assetUtils } from '@zen/common-utils';
import * as OrderConverter from '../../../utils/OrderConverter';
import getMessageBody from './getMessageBody';

const { toKalapas } = assetUtils;

const emptyFunc = () => {};

/**
 * Exposes all actions to execute the DEX contract
 */
export default function useDexExecutionStore({ dbClient }) {
  const {
    state: {
      keys: { pubKey },
      wallet,
    },
    actions: { executeAndPublish, fetchTransactions },
  } = WalletStore.useStore();
  const {
    state: {
      settings: { contractId, chain },
    },
  } = SettingsStore.useStore();

  const make = useCallback(
    async ({ isBuy, base, quote, quantity, total, password } = {}) => {
      const order = OrderConverter.getMakeOrder({
        isBuy,
        base,
        quote,
        quantity: toKalapas(quantity),
        total: toKalapas(total),
      });
      const yaml = getMessageBody(
        Object.assign({}, order, {
          makerPubKey: pubKey,
          makeSide: isBuy ? 'buy' : 'sell',
        })
      );

      return await executeAndPublish({
        address: ZenJsUtils.getContractAddress(chain, contractId),
        command: 'Make',
        includeReturnAddress: false,
        messageBody: yaml,
        spends: [
          {
            asset: order.underlyingAsset,
            amount: order.underlyingAmount,
          },
        ],
        password,
      }).catch(throwWithClientError);
    },
    [chain, contractId, executeAndPublish, pubKey]
  );

  const cancel = useCallback(
    async ({ order, password } = {}) => {
      return await executeAndPublish({
        address: ZenJsUtils.getContractAddress(chain, contractId),
        command: 'Cancel',
        includeReturnAddress: false,
        messageBody: getMessageBody({ ...order, ...OrderConverter.orderAmountsToKalapas(order) }), // override with kalapas
        password,
      }).catch(throwWithClientError);
    },
    [chain, contractId, executeAndPublish]
  );

  const take = useCallback(
    async ({ order, base, quantity, total, password } = {}) => {
      const takeSide = OrderConverter.isBuy({ base, order }) ? 'buy' : 'sell';
      const takeOrder = OrderConverter.getTakeOrder({
        order: { ...order, ...OrderConverter.orderAmountsToKalapas(order) }, // override with kalapas
        base,
        quantity: toKalapas(quantity),
        total: toKalapas(total),
      });
      const yaml = getMessageBody({ ...takeOrder, takeSide });

      return await executeAndPublish({
        address: ZenJsUtils.getContractAddress(chain, contractId),
        command: 'Take',
        includeReturnAddress: true,
        messageBody: yaml,
        spends: [
          {
            asset: takeOrder.pairAsset,
            amount: takeOrder.providedAmount,
          },
        ],
        password,
      }).catch(throwWithClientError);
    },
    [chain, contractId, executeAndPublish]
  );

  return useMemo(
    () => ({
      actions: {
        createOrder: wallet
          ? withAwaitDbRefresh({
              dbClient,
              f: make,
              fetchTransactions,
            })
          : emptyFunc,
        cancelOrder: wallet
          ? withAwaitDbRefresh({
              dbClient,
              f: cancel,
              fetchTransactions,
            })
          : emptyFunc,
        takeOrder: wallet
          ? withAwaitDbRefresh({ dbClient, f: take, fetchTransactions })
          : emptyFunc,
      },
    }),
    [cancel, dbClient, fetchTransactions, make, take, wallet]
  );
}

/**
 * Returns a new function which executes the given function and then calls refresh
 */
var withAwaitDbRefresh = ({ f: executionFunction, dbClient, fetchTransactions } = {}) => async (
  data
) => {
  await executionFunction(data);

  await Promise.all([dbClient.refresh(), fetchTransactions()]);
  return;
};

// TODO: change this to actually tell what happened
function throwWithClientError(error) {
  if (error && error.message === 'contract is not active') {
    throw error;
  }

  if (error && error.message === 'not enough tokens') {
    throw new Error('Not enough tokens');
  }

  throw new Error('Corrupt message body');
}
